import React from "react";
import * as d3 from "d3";
import { ScaleLinear } from "d3";

import { DatasetRecord, LineChartDataset } from "./LineChart";
import { Bounds2D } from "../LineChartUtils";

interface LineProps {
  bounds: Bounds2D;
  color?: string;
  dataset: LineChartDataset;
  hoveredRecords?: Array<DatasetRecord>;
  showDots?: boolean;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
}

export default function Line(props: LineProps): React.JSX.Element {
  const lineBuilder = d3.line<DatasetRecord>()
    .x((data) => {
      return props.xScale(data?._x ?? 0);
    })
    .y((data) => {
      return props.yScale(data._y ?? 0);
    })
    .curve(d3.curveMonotoneX);

  const dotRadius = 3;
  const dotRadiusHovered = 6;
  const lineMin = props.xScale(props.dataset.records[0]._x);
  const lineMax = props.xScale(props.dataset.records[props.dataset.records.length-1]._x);
  const lineWidthRatio = (lineMax - lineMin) / props.bounds.width;
  const dotSpacingMinimum = (dotRadius + dotRadiusHovered) + 1;
  const tooNarrowToShowDots = ((props.dataset.records.length-1) * dotSpacingMinimum / lineWidthRatio) > props.bounds.width;
  const showDots = props.showDots && !tooNarrowToShowDots;
  
  const linePath = lineBuilder(props.dataset.records) ?? "";
  return (
    <g
      name="line-area"
      transform={`translate(${props.bounds.x}, ${props.bounds.y})`}
    >
      <path
        d={linePath}
        stroke={props.color ?? "currentColor"}
        fill="none"
        strokeWidth={2}
      />

      {props.dataset.records.map((record, index) => {
        let isHovered = !!props.hoveredRecords?.includes(record);
        let x = props.xScale(record?._x ?? 0);
        let y = props.yScale(record?._y ?? 0);

        if (record.renderDot) {
          return record.renderDot({ color: props.dataset.color, index, isHovered, record, x, y });
        }

        if (!isHovered && !showDots) {
          return null;
        }

        return (
          <circle
            key={`circle_${index}`}
            cx={x}
            cy={y}
            fill={props.color ?? "currentColor"}
            fillOpacity={1}
            opacity={1}
            r={isHovered ? dotRadiusHovered : dotRadius}
            stroke={props.color ?? "currentColor"}
            strokeWidth={2}
          />
        )
      })}
    </g>
  )
}